import React, { useState } from "react";
import Dropzone, { DropzoneState } from "react-dropzone";
import UploadedContainer from "./UploadedContainer";
import Image from "../images/Image";
import { handleDelete, handleUpload } from "./S3Function";
import ErrorMessage from "../ErrorMessages/ErrorMessage";
import RoundedLoader from "../loaders/RoundedLoader";
import InputLabel from "../inputfields/InputLabel";
import { breadcrumbsContent } from "../breadcrumbs/BreadcrumbsContent";
import useTranslation from "../CustomHooks/useTranslation";

interface FileWithPreview extends File {
  preview: string;
}

interface S3ImageUploadProps {
  heading: string;
  disabled: boolean;
  UploadPath: string;
  upload_id: string;
  handleUpload: (id: string, value: string) => void;
  uploadedFile: string;
  label?: string;
  errorMessage?: string;
  required?: boolean;
  id?: string;
  setCustomError?: (error: any) => void;
  paddingTop?: string;
}

const validateFile = (file: File): string | null => {
  const validFormats = ["image/jpeg", "image/png", "application/pdf"];
  if (!validFormats.includes(file.type)) {
    return "Invalid file format.";
  }
  if (file.size > 10 * 1024 * 1024) {
    return "Upload Failed: File exceeds 10MB.";
  }
  return null;
};

const processFile = async (
  file: File,
  setFile: React.Dispatch<React.SetStateAction<FileWithPreview | null>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setUploading: React.Dispatch<React.SetStateAction<boolean>>,
  uploadPath: string,
) => {
  const validationError = validateFile(file);
  if (validationError) {
    setError(validationError);
    return;
  }

  setFile(
    Object.assign(file, {
      preview: URL.createObjectURL(file),
    })
  );

  setUploading(true);
  try {
    const uploadedUrl = await handleUpload(file, uploadPath);
    if (uploadedUrl) {
      return uploadedUrl
    } else {
      setError("Upload failed.");
    }
  } catch {
    setError("Upload failed.");
  } finally {
    setUploading(false);
  }
};

const S3ImageUpload: React.FC<S3ImageUploadProps> = ({
  heading,
  disabled,
  UploadPath,
  upload_id,
  handleUpload,
  uploadedFile,
  label,
  errorMessage,
  required,
  paddingTop,
  id,
  setCustomError
}) => {
  const [file, setFile] = useState<FileWithPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const { translate, notEnglish } = useTranslation(breadcrumbsContent)
  const handleDrop = async (acceptedFiles: File[], rejectedFiles: any[]) => {
    setError(null);

    if (rejectedFiles.length > 0) {
      const rejectedFile = rejectedFiles[0].file;
      setError(validateFile(rejectedFile) || "File upload failed.");
      return;
    }

    if (acceptedFiles.length > 0) {
      const uploadedFile = await processFile(
        acceptedFiles[0],
        setFile,
        setError,
        setUploading,
        UploadPath,
      );
      if (uploadedFile !== undefined) {
        handleUpload(upload_id, uploadedFile)
      }
    }
  };

  const handleManualUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    if (event.target.files && event.target.files.length > 0) {
      const uploadedFile = await processFile(
        event.target.files[0],
        setFile,
        setError,
        setUploading,
        UploadPath,
      )
      if (uploadedFile !== undefined) {
        handleUpload(upload_id, uploadedFile)
      }
    }
  };

  const handleClickUpload = () => {
    setError(null);
    setCustomError?.({});
    setTimeout(() => {
      const input = document.getElementById(id || upload_id) as HTMLInputElement;
      if (input) {
        input.value = ""; // Reset value
        input.click();
      }
    }, 0); // Delay to ensure it's inside a user-initiated event
  };



  return (
    <div className={`flex flex-col ${paddingTop ? paddingTop : 'pt-8'}`} dir={notEnglish ? 'rtl' : 'ltr'}>
      {label && <InputLabel required={required} labelName={label || ''} id={label?.toLowerCase() || ''} />}
      {heading !== '' && <h1 className="text-heading3 text-primary_text underline mb-6">
        {translate(heading)}
      </h1>}
      {!uploadedFile || uploading ? (
        <Dropzone
          onDrop={handleDrop}
          disabled={disabled || uploading}
          accept={{
            "image/jpeg": [".jpg", ".jpeg"],
            "image/png": [".png"],
            "application/pdf": [".pdf"],
          }}
          maxSize={10 * 1024 * 1024}
          multiple={false}
        >
          {({ getRootProps, isDragActive }: DropzoneState) => (
            <div
              {...getRootProps()}
              className={`w-full max-w-md p-6 border-2 border-dashed rounded-lg flex flex-col items-center justify-center text-center transition ${isDragActive
                ? "border-P_light_active bg-blue-50"
                : error || errorMessage
                  ? "border-negative bg-white"
                  : disabled
                    ? "border-disable_stroke bg-disable_fill"
                    : "border-p_light_hover bg-white"
                }`}
            >
              <input
                type="file"
                id={id || upload_id}
                disabled={disabled || uploading}
                style={{ display: "none" }}
                onChange={handleManualUpload}
              />
              <button
                className={` ${notEnglish ? 'h-[52px] gap-3 px-2 w-fit' : 'h-[44px] gap-2 w-[110px]'} flex items-center justify-center rounded-lg border border-p_primary text-p_primary text-body1 transition`}
                onClick={handleClickUpload}
                aria-label="Upload Button"
                data-testid={id || upload_id}
              >
                {uploading ? (
                  <RoundedLoader />
                ) : (
                  <>
                    <div className={`-rotate-90 `}>
                      <Image src="log-out" />
                    </div>
                    {translate('Upload')}
                  </>
                )}
              </button>
              <p className="text-secondary_text text-medium mb-2">{translate('or')}</p>
              <p className="text-body1 text-primary_text mb-4">{translate('Drag & Drop')}</p>
              <p className="text-secondary_text text-body1">
                {translate('Max file size: 10MB')}
                <br />
                {translate('Supported file types: jpg, png, jpeg and pdf format')}
              </p>
            </div>
          )}
        </Dropzone>
      ) : (
        <UploadedContainer
          fileName={uploadedFile.split('/')[uploadedFile.split('/').length - 1]}
          onclickDelete={() => {
            handleDelete(uploadedFile)
            handleUpload(upload_id, '')
            setFile(null);
          }}
          handleManualUpload= {handleManualUpload}
          id={upload_id}
        />
      )}
      {(error || errorMessage) && <ErrorMessage errorMessage={(error || errorMessage) || ''} className="text-negative" />}
      {/* {!error && errorMessage && <ErrorMessage errorMessage={errorMessage || ''} className="text-negative" />} */}
    </div>
  );
};

export default S3ImageUpload;
